import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Flame } from "lucide-react";

/**
 * StreakRibbon
 *
 * Slim green banner shown at the top of the Dashboard when a Pro user has a
 * live under-threshold streak going. Surfaces the retention win daily so users
 * see progress even between milestone celebration emails.
 *
 * Only renders when:
 *   - user is on Pro (Free users don't have streaks)
 *   - streak_days >= 1 (don't demotivate users starting out)
 */
const MILESTONES = [7, 14, 30, 60, 100, 365];

const StreakRibbon = ({ streakDays, threshold, plan }) => {
  if (plan !== "pro") return null;
  const days = Number(streakDays || 0);
  if (days < 1) return null;

  const nextMilestone = MILESTONES.find((m) => m > days) || null;
  const daysToNext = nextMilestone ? nextMilestone - days : null;
  const prevMilestone = [...MILESTONES].reverse().find((m) => m <= days) || 0;
  const progress = nextMilestone
    ? Math.min(1, (days - prevMilestone) / (nextMilestone - prevMilestone))
    : 1;

  const iconKind = days >= 30 ? "flame" : "sparkles";

  return (
    <motion.section
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="dashboard-streak-ribbon"
      className="mb-4 sm:mb-5"
    >
      <div className="rounded-2xl border border-[#00E676]/30 bg-gradient-to-r from-[#00E676]/[0.08] via-[#00E676]/[0.04] to-transparent px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#00E676]/15 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0">
          {iconKind === "flame" ? (
            <Flame size={18} className="text-[#00E676]" />
          ) : (
            <Sparkles size={18} className="text-[#00E676]" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="text-lg sm:text-xl font-bold text-white leading-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="streak-ribbon-days"
            >
              <span className="text-[#00E676]">{days}</span>{" "}
              day{days === 1 ? "" : "s"}{" "}
              <span className="text-slate-300 text-sm sm:text-base font-medium">
                under {threshold || 30}%
              </span>
            </div>
          </div>
          {nextMilestone ? (
            <div className="mt-1.5 sm:mt-2">
              <div
                className="text-[10px] uppercase tracking-[0.2em] text-slate-300 font-semibold mb-1"
                data-testid="streak-ribbon-next-milestone"
              >
                Next milestone in {daysToNext} day{daysToNext === 1 ? "" : "s"} · {nextMilestone}-day streak
              </div>
              <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden">
                <div
                  className="h-full bg-[#00E676] transition-all duration-500"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>
          ) : (
            <div
              className="mt-1 text-[11px] text-slate-300 font-medium"
              data-testid="streak-ribbon-max-milestone"
            >
              You've cleared every milestone — elite discipline.
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default StreakRibbon;
